<template>
  <div class="borrowerAttributeNumber">
    <BorrowerAttributeConstructor
      :questionText="questionText"
      :description="description"
      :descriptionTooltip="descriptionTooltip"
      :disclaimer="disclaimer">
      <template v-slot:input>
        <div :class="{'form-floating': formFloating}" class="ba-number">
          <label class="pb-2" v-if="!formFloating" :for="question.labelId || 'ba' + identifier">
            <span>{{ label }}</span>
          </label>
          <input
            :id="question.labelId || 'ba' + identifier"
            :name="question.labelId || 'ba' + identifier"
            class="form-control"
            inputmode="numeric"
            :class="{
              'is-invalid': v$.value && v$.value.$invalid && v$.value.$dirty,
              'disabled': disabled,
              'disabled-repressed': disabled && repressDisabledStyle
            }"
            :disabled="disabled || question.group === 'bankValues'"
            :maxlength="question.maxLength"
            :minlength="question.minLength"
            :placeholder="question.placeholder || (formFloating ? label : '')"
            :aria-label="label"
            :required="question.required"
            v-model="value"
            v-maska:[phoneOptions] :data-maska="retrieveMask" :data-maska-tokens="retrieveTokens"
            @blur="onBlur(v$.value, $event)"
            @keyup="!singleForm && onChange(v$.value, $event, false)"
            @keydown.enter="onChange(v$.value, $event, true)"
            :data-cy="question.alias"
            :dreams-id="question.alias"
          />
          <label class="type-400" v-if="formFloating" :for="'ba' + identifier">
            <span>{{ label }}</span>
          </label>

          <LendioBootstrapTooltip v-if="tooltipText">
              <span class="text-tooltip-icon position-absolute text-primary" data-bs-toggle="tooltip" data-bs-placement="right" :title="tooltipText">
                <fa-icon :icon="['fal', 'circle-info']"></fa-icon>
              </span>
          </LendioBootstrapTooltip>
          <small class="invalid-feedback" v-if="get(v$, 'value.$error')">
            <fa-icon :icon="['fal', 'circle-exclamation']"></fa-icon> {{ errorText }}
          </small>
        </div>
      </template>
    </BorrowerAttributeConstructor>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { mapState } from 'pinia'
import { required, minLength, maxLength, between, numeric } from '@vuelidate/validators'
import get from 'lodash/get'
import BaseBorrowerAttributeMixin from "./BaseBorrowerAttributeMixin";
import { useQuestionsStore } from '~/store/questions'

export default {
  props: {
    question: Object,
    alias: String,
    disabled: Boolean,
    repressDisabledStyle: Boolean,
    updateStoreValue: Function,
    borrowerInterest: String,
  },
  emits: ['invalidity','answer'],
  setup: () => ({ v$: useVuelidate() }),
  data () {
    return {
      errorMessage: 'This field is invalid',
      nonFloatingInputs: [
        'widget.customer_visit_affected_period',
        'widget.customer_visits_comparable_period',
        'widget.customers_served_affected_period',
        'widget.customers_served_comparable_period',
        'widget.contracts_affected_period',
        'widget.contracts_comparable_period_',
        'widget.projects_affected_period',
        'widget.projects_comparable_period',
        'widget.production_affected_period',
        'widget.production_comparable_period',
        'widget.capacity_affected_period',
        'widget.capacity_comparable_period',
        'widget.average_employees_2020',
        'widget.average_employees_2021',
      ]
    }
  },
  computed: {
    ...mapState(useQuestionsStore, ['singleForm']),
    identifier() {
      return this.question.id || this.question.alias
    },
    invalid() {
      return this.v$.$invalid
    },
    errorText() {
      return get(this.v$, 'value.$error') ? this.errorMessage : ''
    },
    retrieveMask() {
      return this.getMask(this.question.alias, this.question.type);
    },
    retrieveTokens() {
      return this.getTokens(this.question.type)
    },
    formFloating() {
      if (this.nonFloatingInputs.includes(this.question.alias)) {
        return null
      }
      return true
    },
    phoneOptions(){
      const options = {
        preProcess: val => {
          if(this.question.alias === 'contact.mobilePhone' || this.question.alias === 'borrower.phone'){
            if(!val) return ''
            return this.formatPhoneNumber(val)
          }
          return val
        }
      }
      return options
    },
  },
  created () {
    this.$watch('invalid', (invalid) => {
      this.$emit('invalidity', invalid)
    }, {immediate: true})

    switch (this.question.alias) {
      case 'term':
        this.errorMessage = 'Please enter a number between 1 and 999'
        break
      case 'interestRate':
        this.errorMessage = 'Please enter a number between 0 and 100'
        break
      case 'creditScore':
        this.errorMessage = 'Please enter a number between 300 and 999'
        break
      case 'days_negative_per_month':
        this.errorMessage = 'Please enter a number between 0 and 31'
        break
      case 'daysNegativeAvg3Month':
        this.errorMessage = 'Please enter a number between 0 and 93'
        break
      case 'number_of_employees':
        this.errorMessage = 'Please enter a number between 0 and 999999'
        break
      case 'average_employees_2019':
      case 'average_employees_2020':
      case 'average_employees_2021':
        this.errorMessage = 'Please enter a number between 0 and 999999'
        break
      default:
        this.errorMessage = 'Please enter a number'
    }
  },
  mixins: [BaseBorrowerAttributeMixin],
  validations: function () {
    if (this.question.type === 'decimal') {
      return {
        value: { required, minLength: minLength(1), between: between(0, 100) }
      }
    }
    if (this.question.alias === 'creditScore') {
      return {
        value: { required: required, minLength: minLength(3), maxLength: maxLength(3), between: between(300, 999) }
      }
    } else if (this.question.alias === 'days_negative_per_month') {
      return {
        value: { required: required, minLength: minLength(1), maxLength: maxLength(2), between: between(0, 31) }
      }
    } else if (this.question.alias === 'term') {
      return {
        value: { required: required, minLength: minLength(1), between: between(0, 999) }
      }
    } else if (this.question.alias === 'borrower.phone' || this.question.alias === 'contact.mobilePhone') {
      return {
        value: { required, minLength: val => this.phoneNumberCheck(val) }
      }
    } else if(this.question.alias === 'number_of_employees') {
      return {
        value: { required: required, minLength: minLength(1), numeric: numeric, pattern: (val) => this.customValidation(val), between: between(0, 999999) }
      }
    } else if(['average_employees_2019', 'average_employees_2020', 'average_employees_2021'].includes(this.question.alias)) {
      return {
        value: { required: required, minLength: minLength(1), maxLength: maxLength(6), numeric: numeric }
      }
    } else {
      return {
        value: { required: required, minLength: minLength(1), numeric: numeric }
      }
    }
  },
  methods: {
    formatPhoneNumber(value) {
      if(value){
         // Remove non-digit characters
        let phoneNumber = value.replace(/\D+/g, '');

      // Check if the number includes a country code (e.g., '1' for US/Canada)
      const isCountryCode = phoneNumber.length === 11 && phoneNumber.startsWith('1');

      // Remove the country code if present
      if (isCountryCode) {
        phoneNumber = phoneNumber.substring(1);
      }

      // Truncate to 10 digits in case the input is longer
      return phoneNumber.substring(0, 10);
      }
    },
    get() {
      return get(...arguments)
    },
    phoneNumberCheck(value) {
      // phone is 14 characters long and is not numeric with special chars: '() -' included
      const numericValue = value.toString().replace(/[\(\)\-\W]/g, '') // Remove '() -' chars
      return value.length >= 14 || numericValue.length >= 10
    },
    customValidation(value) {
      switch (this.question.alias) {
        case 'number_of_employees':
          let valid = parseInt(value) >= 0
          return valid
        default:
            return true;
        }
    },
    getMask: (alias, type) => {
      if (type === 'decimal') {
        return '&'
      }

      switch (alias) {
        case 'creditScore':
        case 'percentOwnership':
          return '###'
        case 'days_negative_per_month':
        case 'processing_days_per_month':
          return '##'
        case 'borrower.phone':
        case 'contact.mobilePhone':
          return '(###) ###-####'
        case 'number_of_employees':
          return '######'
        default:
          return '################################'
      }
    },
    getTokens: (type) => {
      if (type === 'decimal') {
        return '&:[0-9\.]:repeated'
      }
    },
    toFixedMax(value, fixedLen = 2) {
      const [beforeDecimal, afterDecimal] = value.split('.')
      if (afterDecimal && afterDecimal.length > fixedLen) {
        // Regex: 1 or more digits, proceeded by a '.' with up to fixedLength of digits
        const maxDecimalLength = new RegExp('^\\d+(?:\\.\\d{0,' + fixedLen + '})?')
        const match = value.match(maxDecimalLength)
        return match ? match[0] : value
      }
      return value
    },
    removeExtraPeriods(value) {
      return value.replace(/\.(?=.*\.)/g, "")
    },
    handleDecimal(value) {
      value = this.removeExtraPeriods(value)
      value = this.toFixedMax(value, 2)
      return value
    },
    onBlur(v$, e) {
      if (this.question.type === 'decimal') {
        e.target.value = this.handleDecimal(e.target.value)
        this.value = e.target.value
      }

      v$.$touch()
      let postValue = e.target.value
      if (this.singleForm) {
        this.$emit('answer', { value: parseInt(postValue, 10), isInvalid: v$.$error, submitImmediately: false })
      }

      if (typeof postValue !== 'string' || !postValue.indexOf('%')) {
        this.value = this.value === postValue ? postValue + '%' : this.value
      }
    },
    onChange (v$, e, submitImmediately) {
      if (this.question.type === 'decimal') {
        e.target.value = this.handleDecimal(e.target.value)
        this.value = e.target.value
      }

      let postValue = e.target.value
      if (this.question.alias === 'borrower.phone' || this.question.alias === 'contact.mobilePhone') {
        // Remove anything that isn't a digit before post
        postValue = postValue.replace(/\D/g, '')
      }

      if (this.question.type === 'decimal') {
        postValue = parseFloat(postValue)
      } else {
        postValue = parseInt(postValue, 10)
      }

      this.$emit('answer', { value: postValue, isInvalid: v$.$error, submitImmediately })
    }
  }
}
</script>

<style scoped lang="scss">
.text-tooltip-icon {
  top: 0.9rem;
  right: 1rem;
}
</style>
